import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Star, Check, MessageSquare, X } from "lucide-react";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/panel/ui";
import Pagination from "@/components/panel/Pagination";
import usePaginatedList from "@/hooks/usePaginatedList";
import { Button, Card, PageHeader } from "@/components/ui-kit";

const STATUS_CLS = { pendente: "bg-amber-50 text-amber-700", aprovada: "bg-emerald-50 text-emerald-700", rejeitada: "bg-red-50 text-red-600" };

const FILTROS = [["pendente", "Pendentes"], ["aprovada", "Aprovadas"], ["rejeitada", "Rejeitadas"], ["", "Todas"]];

const VAZIO = {
  pendente: { title: "Nenhuma avaliação pendente", desc: "Tudo moderado. Avaliações novas da loja aparecem aqui para aprovação." },
  aprovada: { title: "Nenhuma avaliação aprovada", desc: "As que você aprovar ficam visíveis na página do produto." },
  rejeitada: { title: "Nenhuma avaliação rejeitada", desc: "As rejeitadas não aparecem na loja, mas seguem aqui no histórico." },
  "": { title: "Nenhuma avaliação ainda", desc: "Clientes avaliam pela página do produto, depois da compra." },
};

export default function Reviews() {
  const [filter, setFilter] = useState("pendente");
  const [moderatingId, setModeratingId] = useState(null);

  // Status vai como filtro do servidor: filtrar em memória só enxergaria a
  // página atual, escondendo pendentes que caíssem na página 2.
  const {
    items: reviews, total, page, lastPage, setPage,
    isLoading: loading, isError: error, refetch: load,
  } = usePaginatedList("/admin/reviews", { status: filter || undefined });

  const moderate = async (r, status) => {
    setModeratingId(r.id);
    try { await api.patch(`/admin/reviews/${r.id}`, { status }); toast.success(`Avaliação ${status}`); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setModeratingId(null); }
  };

  return (
    <div className="space-y-5" data-testid="admin-reviews-page">
      <PageHeader
        eyebrow="Relacionamento"
        title="Avaliações"
        icon={MessageSquare}
        description="Moderação das avaliações da loja. Só as aprovadas aparecem na página do produto."
        filters={(
          // `flex-1` nas abas: no celular a fileira ocupa a largura toda e cada
          // aba vira um alvo de toque de verdade. Antes eram 28px de altura,
          // abaixo do mínimo, e as quatro se espremiam num canto.
          // Sem `flex-1`: com largura igual, a aba ativa (que ganha a contagem)
          // era a mais larga e quebrava em duas linhas. No tamanho natural as
          // quatro somam ~273px e sobram na largura do celular.
          <div role="tablist" aria-label="Filtrar avaliações"
            className="w-full md:w-auto flex justify-between md:justify-start bg-white border border-slate-200 rounded-xl p-1 gap-0.5">
            {FILTROS.map(([v, l]) => (
              <button key={v} onClick={() => setFilter(v)} data-testid={`reviews-filter-${v || "all"}`}
                role="tab" aria-selected={filter === v}
                className={`whitespace-nowrap text-xs font-semibold px-2.5 md:px-3 h-9 rounded-lg transition-colors ${
                  filter === v ? "bg-brand-600 text-white" : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"}`}>
                {/* Só a aba ativa mostra contagem: com filtro no servidor a tela não
                    conhece o total das outras, e o número antigo era só da página. */}
                {l}{filter === v && <span className="opacity-70"> {total}</span>}
              </button>
            ))}
          </div>
        )}
      />
      {error ? <ErrorState onRetry={load} /> : loading ? <div className="bg-white border border-slate-200 rounded-xl"><TableSkeleton cols={3} /></div> : (
      <div className="space-y-3">
        {reviews.map((r) => (
          <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-start justify-between gap-4" data-testid="review-row">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-slate-900">{r.user_name}</span>
                <span className="flex text-amber-400">{Array.from({ length: 5 }, (_, i) => <Star key={i} size={13} fill={i < r.rating ? "currentColor" : "none"} />)}</span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_CLS[r.status]}`}>{r.status}</span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">{r.product_name} · {new Date(r.created_at).toLocaleDateString("pt-BR")}</p>
              <p className="text-sm text-slate-600 mt-1.5">{r.comment}</p>
            </div>
            {r.status === "pendente" && (
              // `size="icon"` (40px) no lugar de `p-2` com ícone de 15px, que
              // dava 31px — abaixo do alvo de toque e menor que os botões de
              // ação de todas as outras telas.
              <div className="flex gap-2 shrink-0">
                <Button size="icon" loading={moderatingId === r.id} onClick={() => moderate(r, "aprovada")}
                  data-testid="review-approve-btn" aria-label={`Aprovar avaliação de ${r.user_name}`}
                  icon={Check} className="bg-emerald-600 hover:bg-emerald-700" />
                <Button size="icon" variant="danger" loading={moderatingId === r.id} onClick={() => moderate(r, "rejeitada")}
                  data-testid="review-reject-btn" aria-label={`Rejeitar avaliação de ${r.user_name}`} icon={X} />
              </div>
            )}
          </div>
        ))}
        {reviews.length === 0 && (
          // Era uma frase cinza solta. O EmptyState é o padrão do painel — e
          // aqui ele também explica o que cada aba significa.
          <Card><EmptyState icon={MessageSquare} testId="reviews-empty" {...VAZIO[filter]} /></Card>
        )}
        <Pagination page={page} lastPage={lastPage} total={total} onChange={setPage} label="avaliações" />
      </div>
      )}
    </div>
  );
}
